"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";

import { useAuth } from "@/context/auth-context";
import { isAdmin, isAdvertiser, isEmployee, isStudent, isTutor } from "@/lib/role-utils";
import {
  FEATURE_TOUR_SKIP_PATHS,
  SOCIAL_NUDGE_WINDOW_MS,
  type FeatureTourRole,
} from "@/constants/feature-tour";
import { FeatureTour } from "@/components/feature-tour";
import { SocialNudge } from "@/components/social-nudge";
import { FoundingWelcomeModal } from "@/components/founding-welcome-modal";

type FeatureHints = {
  role?: string;
  createdAt?: string;
  firstLoginAt?: string | null;
  featureTourCompletedAt?: string | null;
  socialNudgeDismissedAt?: string | null;
  onboardingCompleted?: boolean;
  hasSocialEngagement?: boolean;
  isFoundingMember?: boolean;
  foundingWelcomeDismissedAt?: string | null;
};

function resolveTourRole(
  user: unknown,
  hints: FeatureHints | null,
  forceTour: boolean,
): FeatureTourRole | null {
  if (isAdvertiser(user)) return "advertiser";
  if (isTutor(user)) {
    const status = String((user as { onboardingStatus?: string }).onboardingStatus || "").toUpperCase();
    if (!forceTour && status !== "APPROVED") return null;
    return "tutor";
  }
  if (isAdmin(user) || isEmployee(user)) return null;
  if (isStudent(user)) {
    const completed =
      hints?.onboardingCompleted === true ||
      (user as { onboardingCompleted?: boolean }).onboardingCompleted === true;
    if (!forceTour && !completed) return null;
    return "student";
  }
  return null;
}

function withinSocialWindow(hints: FeatureHints): boolean {
  const stamp = hints.firstLoginAt || hints.createdAt;
  if (!stamp) return false;
  const t = new Date(stamp).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t < SOCIAL_NUDGE_WINDOW_MS;
}

function persistFoundingWelcomeDismissed() {
  fetch("/api/user/preferences", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ foundingWelcomeDismissed: true }),
  }).catch(() => {});
}

export function ProductHints() {
  const { user, isAuthenticated } = useAuth();
  const pathname = usePathname();
  const [hints, setHints] = useState<FeatureHints | null>(null);
  const [tourDone, setTourDone] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const [forceTour, setForceTour] = useState(false);
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);
  const tourLockRef = useRef(false);

  const skipPage = FEATURE_TOUR_SKIP_PATHS.some((p) => pathname.startsWith(p));

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const force = params.get("tour") === "1";
    setForceTour(force);
    if (force) {
      setTourDone(false);
      tourLockRef.current = false;
    }
  }, [pathname]);

  useEffect(() => {
    if (skipPage) {
      setHints(null);
      return;
    }
    if (!isAuthenticated || !user) return;
    let cancelled = false;
    fetch("/api/user/feature-hints")
      .then((r) => (r.ok ? r.json() : { featureTourCompletedAt: null }))
      .then((data) => {
        if (!cancelled && data) setHints(data);
      })
      .catch(() => {
        if (!cancelled) setHints({ featureTourCompletedAt: null });
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user, skipPage]);

  if (!isAuthenticated || !user || skipPage) return null;
  if (!hints && !forceTour) return null;

  const role = resolveTourRole(user, hints, forceTour);
  const welcomeRole = isTutor(user) ? "tutor" : isStudent(user) ? "student" : null;
  const tourAlreadyDone = tourDone || tourLockRef.current || Boolean(hints?.featureTourCompletedAt);
  const showFoundingWelcome =
    !forceTour &&
    !welcomeDismissed &&
    !tourAlreadyDone &&
    hints?.isFoundingMember === true &&
    !hints.foundingWelcomeDismissedAt &&
    Boolean(welcomeRole) &&
    (isTutor(user)
      ? String((user as { onboardingStatus?: string }).onboardingStatus || "").toUpperCase() === "APPROVED"
      : hints.onboardingCompleted === true ||
        (user as { onboardingCompleted?: boolean }).onboardingCompleted === true);

  const dismissWelcome = () => {
    setWelcomeDismissed(true);
    persistFoundingWelcomeDismissed();
  };

  const showTour =
    Boolean(role) &&
    !tourDone &&
    !showFoundingWelcome &&
    (forceTour || !hints?.featureTourCompletedAt);
  if (showTour) tourLockRef.current = true;
  const showNudge =
    !showTour &&
    !forceTour &&
    !nudgeDismissed &&
    !hints?.socialNudgeDismissedAt &&
    !hints?.hasSocialEngagement &&
    Boolean(hints && withinSocialWindow(hints)) &&
    (isStudent(user) || isTutor(user)) &&
    !isAdvertiser(user) &&
    !isAdmin(user) &&
    !isEmployee(user);

  return (
    <>
      {showFoundingWelcome && welcomeRole && (
        <FoundingWelcomeModal role={welcomeRole} open onDismiss={dismissWelcome} />
      )}
      {showTour && role && (
        <FeatureTour
          role={role}
          preview={forceTour}
          onComplete={() => {
            tourLockRef.current = true;
            setTourDone(true);
            setWelcomeDismissed(true);
            setHints((h) =>
              h
                ? {
                    ...h,
                    featureTourCompletedAt: new Date().toISOString(),
                    foundingWelcomeDismissedAt: h.foundingWelcomeDismissedAt || new Date().toISOString(),
                  }
                : h,
            );
            if (!forceTour) persistFoundingWelcomeDismissed();
          }}
        />
      )}
      {showNudge && (
        <SocialNudge user={user} onDismiss={() => setNudgeDismissed(true)} />
      )}
    </>
  );
}
